import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Building2, GraduationCap, UserSearch, ClipboardCheck, BarChart3, Bot, Sparkles } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import LeadForm from '@/components/shared/LeadForm';
import WheelOfTransformation from '@/components/shared/WheelOfTransformation';
import { CLIENT_LOGOS } from '@/lib/constants';

export default function HomePage() {
  return (
    <div className="overflow-hidden bg-white">
      {/* ═══════════════════════════════════════════════════════
          HERO - WITH POWERFUL TRANSFORMATION IMAGE
          ═══════════════════════════════════════════════════════ */}
      <section className="relative min-h-[85vh] flex items-center bg-gradient-to-br from-[#FAF5EF] via-[#FEFBF8] to-white overflow-hidden">
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12 py-24">
          <div className="grid lg:grid-cols-[1.2fr,0.8fr] gap-16 items-center">
            {/* Content - Left Side */}
            <div className="max-w-[750px]">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-8">
                  Business Consulting & Leadership Development
                </p>
                
                <h1 className="text-[clamp(3rem,7vw,6rem)] font-bold text-[#1A1008] leading-[1] tracking-[-0.03em] mb-8">
                  We don't build<br/>
                  strategies on paper.
                </h1>
                
                <p className="text-[clamp(1.75rem,3.5vw,2.75rem)] font-semibold text-[#E8642C] leading-[1.15] tracking-[-0.01em] mb-10">
                  We build organisations<br/>
                  that execute them.
                </p>
                
                <p className="text-xl text-[#6B5B50] leading-[1.65] max-w-[580px] mb-14">
                  From vision to execution. Structure to culture. Leadership to capability. 
                  We transform businesses from the inside out.
                </p>
                
                <div className="flex gap-5 mb-16">
                  <Link
                    to="/contact"
                    className="group bg-[#1A1008] text-white px-9 py-4 rounded-full text-sm font-medium hover:bg-[#3D1C11] transition-all duration-300 flex items-center gap-2.5 shadow-lg hover:shadow-xl"
                  >
                    Start Your Transformation
                    <ArrowUpRight size={16} className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </Link>
                </div>
                
                {/* Trust Metrics */}
                <div className="grid grid-cols-3 gap-6 sm:gap-8 lg:gap-10 max-w-[600px]">
                  <TrustMetric value={739} suffix="+" label="Clients Transformed" />
                  <TrustMetric value={500000} suffix="+" label="Lives Impacted" format />
                  <TrustMetric value={18} suffix=" Yrs" label="Of Excellence" />
                </div>
              </motion.div>
            </div>

            {/* Image - Right Side (hidden on mobile) */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:block relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1775564578285-194db66d9121?w=800&q=80"
                  alt="Transformation journey - from darkness to light"
                  className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1008]/20 to-transparent" />
              </div>
              {/* Accent element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#E8642C] rounded-full opacity-10 blur-3xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          CLIENT LOGOS
          ═══════════════════════════════════════════════════════ */}
      <section className="border-y border-[#3D1C11]/6 py-10 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="flex items-center gap-6 sm:gap-12">
            <p className="text-[11px] font-medium text-[#6B5B50]/60 tracking-[0.15em] uppercase whitespace-nowrap hidden md:block">
              Trusted by
            </p>
            <div className="flex-1 overflow-hidden relative">
              <div className="flex gap-12 sm:gap-16 logo-ticker items-center" style={{ width: 'max-content' }}>
                {[...CLIENT_LOGOS, ...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
                  <div key={i} className="flex-shrink-0 w-24 sm:w-32 h-12 sm:h-14 flex items-center justify-center">
                    <img 
                      src={logo.url} 
                      alt={logo.alt}
                      className="max-w-full max-h-full w-auto h-auto object-contain opacity-30 hover:opacity-60 transition-opacity grayscale"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          WHAT WE DO - WITH SCROLL ANIMATIONS
          ═══════════════════════════════════════════════════════ */}
      <section className="py-32 bg-white relative overflow-hidden">
        {/* Decorative Element */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E8642C]/5 rounded-full blur-3xl" />
        
        <div className="relative mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <ScrollFadeIn>
            <div className="max-w-[800px] mx-auto text-center mb-20">
              <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">
                What We Do
              </p>
              <h2 className="text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold text-[#1A1008] leading-[1.15] tracking-[-0.02em] mb-6">
                We solve the toughest challenges in organisation transformation.
              </h2>
              <p className="text-lg text-[#6B5B50] leading-[1.7]">
                Strategy without execution is hallucination. We help you build the structures, 
                capabilities, and culture to turn vision into measurable results.
              </p>
            </div>
          </ScrollFadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
            {[
              {
                icon: Building2,
                title: 'Consulting',
                desc: 'Organisation design, people strategy, performance systems, M&A integration.',
                href: '/consulting',
                delay: 0
              },
              {
                icon: GraduationCap,
                title: 'Learning & Development',
                desc: 'Leadership programs, executive coaching, transformational experiences.',
                href: '/learning',
                delay: 0.1
              },
              {
                icon: UserSearch,
                title: 'Executive Search',
                desc: 'Finding leaders who transform businesses, not just fill roles.',
                href: '/executive-search',
                delay: 0.2
              },
              {
                icon: ClipboardCheck,
                title: 'Assessments',
                desc: 'Psychometric tools and assessment centres to build leadership pipelines.',
                href: '/assessments',
                delay: 0.3
              },
              {
                icon: BarChart3,
                title: 'Engagement & Research',
                desc: 'X-Model surveys, culture diagnostics, employee engagement studies.',
                href: '/engagement',
                delay: 0.4
              },
              {
                icon: Bot,
                title: 'BOTS.AI',
                desc: 'AI strategy, readiness diagnostics, implementation for the future of work.',
                href: '/botsai',
                delay: 0.5
              },
            ].map((service, i) => (
              <ScrollFadeIn key={i} delay={service.delay}>
                <Link
                  to={service.href}
                  className="group p-8 bg-white border border-[#3D1C11]/8 rounded-2xl hover:border-[#E8642C]/40 hover:shadow-[0_8px_30px_rgba(232,100,44,0.08)] transition-all duration-300 h-full flex flex-col"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#E8642C]/10 flex items-center justify-center mb-6 group-hover:bg-[#E8642C] group-hover:scale-110 transition-all duration-300">
                    <service.icon className="w-6 h-6 text-[#E8642C] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1008] mb-3 group-hover:text-[#E8642C] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[15px] text-[#6B5B50] leading-[1.6] mb-4 flex-grow">
                    {service.desc}
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#E8642C] opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </ScrollFadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          WHEEL OF TRANSFORMATION - ANIMATED
          ═══════════════════════════════════════════════════════ */}
      <WheelOfTransformation />

      {/* ═══════════════════════════════════════════════════════
          PROOF - CASE STUDIES (CLICKABLE WITH ANIMATIONS)
          ═══════════════════════════════════════════════════════ */}
      <section className="py-32 bg-[#FAF5EF] relative overflow-hidden">
        {/* Visual Accent */}
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E8642C]/5 rounded-full blur-3xl" />
        
        <div className="relative mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <ScrollFadeIn>
            <div className="max-w-[750px] mb-20">
              <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">
                Impact
              </p>
              <h2 className="text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold text-[#1A1008] leading-[1.15] tracking-[-0.02em] mb-5">
                Results that matter to<br/>the boardroom.
              </h2>
              <p className="text-lg text-[#6B5B50] leading-relaxed">
                We measure success in business outcomes: revenue growth, cost savings, leadership pipeline strength, and organizational efficiency. Here are real transformation stories from our clients.
              </p>
            </div>
          </ScrollFadeIn>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Case Study 1 */}
            <ScrollFadeIn delay={0}>
              <Link
                to="/impact#specialty-chemicals"
                className="block group bg-white border border-[#3D1C11]/10 rounded-2xl p-10 hover:shadow-2xl hover:border-[#E8642C]/30 transition-all duration-300"
              >
                <div className="mb-6">
                  <div className="text-[10px] font-bold text-[#E8642C] tracking-[0.25em] uppercase mb-4">
                    Specialty Chemicals
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A1008] leading-tight mb-4 group-hover:text-[#E8642C] transition-colors">
                    ₹800 Cr → ₹3,000 Cr<br/>in 36 months
                  </h3>
                  <p className="text-[#6B5B50] text-sm leading-relaxed mb-6">
                    A legacy chemical manufacturer needed scalable structures and leadership capability to support aggressive growth. We restructured the entire organization, built a competency-based framework, and deployed performance management systems that aligned teams to strategic goals. The result: 275% revenue growth without proportional cost increase.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-[#3D1C11]/10">
                  <div>
                    <div className="text-3xl font-bold text-[#E8642C] mb-1">275%</div>
                    <div className="text-[#6B5B50] text-xs uppercase tracking-wide">Revenue Growth</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#E8642C] mb-1">36</div>
                    <div className="text-[#6B5B50] text-xs uppercase tracking-wide">Months</div>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm font-medium text-[#E8642C] opacity-0 group-hover:opacity-100 transition-opacity">
                  Read full case study
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </ScrollFadeIn>

            {/* Case Study 2 */}
            <ScrollFadeIn delay={0.15}>
              <Link
                to="/impact#banking-adc"
                className="block group bg-white border border-[#3D1C11]/10 rounded-2xl p-10 hover:shadow-2xl hover:border-[#E8642C]/30 transition-all duration-300"
              >
                <div className="mb-6">
                  <div className="text-[10px] font-bold text-[#E8642C] tracking-[0.25em] uppercase mb-4">
                    Leading Bank
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A1008] leading-tight mb-4 group-hover:text-[#E8642C] transition-colors">
                    1,175 leaders assessed & developed
                  </h3>
                  <p className="text-[#6B5B50] text-sm leading-relaxed mb-6">
                    A major bank lacked structured tools to assess leadership potential and align talent with strategy. We designed and deployed competency-led virtual Assessment & Development Centres across 4 job scales over 12 months. Every participant received personalized Individual Development Plans (IDPs) to accelerate readiness for future roles.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-[#3D1C11]/10">
                  <div>
                    <div className="text-3xl font-bold text-[#E8642C] mb-1">100%</div>
                    <div className="text-[#6B5B50] text-xs uppercase tracking-wide">Received IDPs</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#E8642C] mb-1">1,175</div>
                    <div className="text-[#6B5B50] text-xs uppercase tracking-wide">Leaders</div>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm font-medium text-[#E8642C] opacity-0 group-hover:opacity-100 transition-opacity">
                  Read full case study
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </ScrollFadeIn>

            {/* Case Study 3 */}
            <ScrollFadeIn delay={0.3}>
              <Link
                to="/impact#auto-components"
                className="block group bg-white border border-[#3D1C11]/10 rounded-2xl p-10 hover:shadow-2xl hover:border-[#E8642C]/30 transition-all duration-300"
              >
                <div className="mb-6">
                  <div className="text-[10px] font-bold text-[#E8642C] tracking-[0.25em] uppercase mb-4">
                    Auto Components
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A1008] leading-tight mb-4 group-hover:text-[#E8642C] transition-colors">
                    ₹1.95 Cr saved annually
                  </h3>
                  <p className="text-[#6B5B50] text-sm leading-relaxed mb-6">
                    An auto component manufacturer had an oversized, inefficient HR function with unclear roles. We redesigned the entire HR structure, reduced manpower by 13% through strategic role consolidation, and improved service delivery. The transformation delivered immediate cost savings while boosting employee satisfaction scores.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-[#3D1C11]/10">
                  <div>
                    <div className="text-3xl font-bold text-[#E8642C] mb-1">₹1.95Cr</div>
                    <div className="text-[#6B5B50] text-xs uppercase tracking-wide">Annual Savings</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#E8642C] mb-1">13%</div>
                    <div className="text-[#6B5B50] text-xs uppercase tracking-wide">Reduction</div>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm font-medium text-[#E8642C] opacity-0 group-hover:opacity-100 transition-opacity">
                  Read full case study
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </ScrollFadeIn>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/impact"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A1008] hover:text-[#E8642C] transition-colors group"
            >
              View all 13 case studies
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          GUINNESS WORLD RECORD
          ═══════════════════════════════════════════════════════ */}
      <section className="py-28 bg-gradient-to-br from-[#1A1008] to-[#2D1810] text-white">
        <div className="mx-auto w-full max-w-[1100px] px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Guinness Logo */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_anexi-consulting/artifacts/gb2w4tod_guinness-world-records-record-holder-logo-gold-small.png"
                alt="Guinness World Records - Record Holder"
                className="w-64 h-auto"
              />
            </div>
            
            {/* Content */}
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-[clamp(2rem,4.5vw,3.25rem)] font-bold leading-[1.15] tracking-[-0.02em] mb-6 text-white">
                1,313 people walked on fire.<br/>
                Simultaneously.
              </h2>
              
              <p className="text-lg text-white/80 leading-relaxed mb-10 max-w-[600px]">
                The largest firewalk ever recorded. A testament to our ability to push boundaries, 
                inspire belief, and create transformation at scale.
              </p>
              
              <Link
                to="/learning#firewalk"
                className="inline-flex items-center gap-2 bg-[#E8642C] text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#CC2929] transition-colors"
              >
                Experience the FireWalk
                <ArrowUpRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          FINAL CTA
          ═══════════════════════════════════════════════════════ */}
      <section className="py-32 bg-white">
        <div className="mx-auto w-full max-w-[850px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <h2 className="text-[clamp(2rem,4vw,3rem)] font-bold text-[#1A1008] leading-[1.2] tracking-[-0.02em] mb-6">
              Ready to transform<br/>your organisation?
            </h2>
            <p className="text-lg text-[#6B5B50] leading-relaxed">
              Let's start a conversation about your challenges and how we can help.
            </p>
          </div>
          
          <LeadForm source="Homepage" />
        </div>
      </section>
    </div>
  );
}

// Trust Metric Component
function TrustMetric({ value, suffix = '', label, format = false }) {
  const { ref, inView } = useInView({ threshold: 0.5, triggerOnce: true });
  return (
    <div ref={ref}>
      <div className="text-2xl sm:text-3xl lg:text-[36px] font-bold text-[#1A1008] tracking-tight mb-1.5 leading-tight">
        {inView ? (
          <CountUp end={value} duration={2.5} separator={format ? "," : ""} suffix={suffix} />
        ) : '0'}
      </div>
      <div className="text-[10px] sm:text-xs text-[#6B5B50]/70 uppercase tracking-wide leading-tight">{label}</div>
    </div>
  );
}

// Scroll Fade In Component
function ScrollFadeIn({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}
